import {
  FileSpreadsheet,
  Gauge,
  MessageSquareText,
  Network,
  Search,
  Shapes,
  ShieldCheck,
  Tags,
  type LucideIcon,
} from 'lucide-react';

interface Capability {
  icon: LucideIcon;
  title: string;
  body: string;
}

// Plain-language capability claims, each true of every TReS site. Wording
// tracks Documentation/public/users/INTRODUCTION.md and the sign-up page:
// a fact CAN carry its source — it is not stamped on everything by default.
const CAPABILITIES: Capability[] = [
  {
    icon: FileSpreadsheet,
    title: 'Start from the files you have',
    body: 'Bring in spreadsheets and exports. The import wizard maps your columns to meaning and remembers the mapping for next time.',
  },
  {
    icon: Shapes,
    title: 'Describe your domain in your own words',
    body: 'Build the vocabulary and structure your subject actually uses. The person who knows the work does it — no coding, no schema language.',
  },
  {
    icon: Tags,
    title: 'Facts can carry their context',
    body: 'Where a fact came from, how far it is trusted, when it applies and to whom — attached to the fact itself, not kept in a side note.',
  },
  {
    icon: MessageSquareText,
    title: 'Ask in plain language',
    body: 'Put questions to your knowledge the way you would ask a colleague. AI is included, and you can use your own provider whenever you prefer.',
  },
  {
    icon: ShieldCheck,
    title: 'Open up any answer',
    body: 'Every answer rests on statements you can read, judge and correct. When the knowledge does not cover a question, you are told so.',
  },
  {
    icon: Search,
    title: 'Find things by meaning',
    body: 'Search across everything you have loaded and get back what you meant, not only what you spelled the same way.',
  },
  {
    icon: Network,
    title: 'See how it all connects',
    body: 'Follow the relationships in a picture, chart what matters on a dashboard, and lay events out on a timeline.',
  },
  {
    icon: Gauge,
    title: 'Know where your data falls short',
    body: 'A readiness check tells you how well your data supports the questions you want to ask — and what to fix first.',
  },
];

// Marketing panes for the welcome page. The capability panes stay static: no
// links, no hover targets. The one path off the page is the Free Trial
// button at the top of it. The lead card says what the platform is for
// before the panes say what it does.
export function CapabilityPanes() {
  return (
    <section aria-labelledby="capabilities-heading" className="mt-12">
      <h2 id="capabilities-heading" className="sr-only">
        What TReS does
      </h2>
      <div className="rounded-xl border border-border bg-card p-6 text-left shadow-sm">
        <p className="text-base font-semibold leading-relaxed text-tres-text-primary">
          TReS is a suite of tools to allow LLMs to answer questions on proprietary data.
        </p>
        <p className="mt-2 text-sm leading-relaxed text-tres-text-secondary">
          Load up data from spreadsheets and other sources and use the LLM Testbed to see how an
          LLM performs. TReS is the comprehensive toolkit for GraphRAG, backed by an innovative
          grounding philosophy that saves time and money and actually works.
        </p>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {CAPABILITIES.map((capability) => (
          <Pane key={capability.title} capability={capability} />
        ))}
      </div>

    </section>
  );
}

function Pane({ capability }: { capability: Capability }) {
  const Icon = capability.icon;
  return (
    <div className="flex h-full flex-col rounded-xl border border-border bg-card p-5 text-left shadow-sm">
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-tres-action/10">
        <Icon className="h-5 w-5 text-tres-action" aria-hidden strokeWidth={1.75} />
      </span>
      <h3 className="mt-4 text-sm font-semibold text-tres-text-primary">{capability.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-tres-text-secondary">{capability.body}</p>
    </div>
  );
}
